<template>
  <UploadFichierState
    :idDemande="store.model?.id ?? 0"
    :type-demande="store.model!.typeDemande"
    :readonly
    :lov-nature-fichiers="lovNatureFichiers"
    :fichiers="model?.fichiers ?? []"
    :natures-fichiers-facultatifs="naturesFichiers.facultatifs"
    :natures-fichiers-obligatoires="naturesFichiers.obligatoires"
    :natures-fichiers-ajout-facultatifs="naturesFichiers.facultatifsDefaut"
    :nombre-fichiers-maximum="naturesFichiers.nombreFichiersMaximum"
    :nombre-fichiers-minimum="naturesFichiers.nombreFichiersMinimum"
    :messages-avertissement="naturesFichiers.messagesAvertissement"
    :fichier-add
    :fichier-remove
  >
    <WrapperForm
      v-model="currentTab"
      :tabs
      show-stepper
    >
      <component :is="currentStepComponent" />
    </WrapperForm>
    <DemandeFooter
      v-if="!readonly || mustConfirm"
      :mustConfirm
      :canRefuse
      :typeDocumentTemplate
      @save="save"
      @transmit="save(LevelRegleEnum.Transmettre)"
      @accept="confirm(true)"
      @refuse="confirm(false)"
      @printDocumentTemplate="downloadDocumentTemplate()"
    />
  </UploadFichierState>
</template>

<script setup lang="ts">
  import type { DemandeDTO, FichierDTO, ModelDemande } from '@/features/api/extranet/dto/Demande/shared'
  import { LevelRegleEnum } from '@/features/domaine/types/opco'
  import {
    DemandeFooter,
    type MetaDemande,
    UploadFichierState,
    useDemandeTabsSummarizer,
    useWatchModel,
  } from '@/features/interface/demande/shared'
  import type { NavigateToDemande, WithNavigateToLocation } from '@/router/navigateTo'
  import { storeToRefs } from 'pinia'
  import { computed, onBeforeUnmount, provide, type Ref, ref } from 'vue'
  import { DEMANDE_STORE } from './DemandeContent.types.ts'
  import { locationToParams } from '@/features/application/locations/locationToParams.ts'
  import { safeCall } from '@/features/shared/tools/function.ts'

  const props = defineProps<WithNavigateToLocation<NavigateToDemande>>()

  // Récupère le store
  const setup = locationToParams(props.navigateToLocation).setup

  const store = setup.useStore()
  const { model, readonly, lov, canRefuse, meta, mustConfirm, naturesFichiers } = storeToRefs(store)

  provide(DEMANDE_STORE, {
    getStore: () => store as any,
    currentStep: computed(() => {
      return currentTab.value as string
    }),
  })

  // Injecte les actions
  const actions = setup.injectActions()
  const { save, confirm, downloadDocumentTemplate, typeDocumentTemplate } = actions

  // Config des tabs
  const currentTab = ref(setup.tabFirst)

  // console.log('meta', meta.value)

  const tabsConfig = setup.tabsErrorConfig(model.value as any) as any
  const { tabs } = useDemandeTabsSummarizer<DemandeDTO<ModelDemande>>(
    tabsConfig,
    meta as Ref<MetaDemande<DemandeDTO<ModelDemande>>>,
  )

  const currentStepComponent = computed(() => (setup.tabsVue as any)[currentTab.value])

  // Config des fichiers
  const fichierAdd = store.setupCustomUpdater<FichierDTO>((patch, fichier) => {
    patch.fichiers!.push(fichier)
  })

  const fichierRemove = store.setupCustomUpdater<FichierDTO>((patch, fichier) => {
    const index = (patch.fichiers as FichierDTO[]).findIndex((item) => item.fichierUid === fichier.fichierUid)
    if (index > -1) {
      patch.fichiers!.splice(index, 1)
    }
  })

  const lovNatureFichiers = computed(() => {
    return setup.getNatureFichiers?.call(null) ?? lov.value.NatureFichier
  })

  // Lance les initialiseurs optionnels
  const disposeCallback = setup.onInititalize?.call(null, actions)

  onBeforeUnmount(() => {
    safeCall(store.$reset)
    if (disposeCallback)
      safeCall(disposeCallback)
  })

  // Trace les changements du modèle
  useWatchModel(model, meta)
</script>
